/* eslint-disable react/require-default-props, no-param-reassign, react/display-name, react/no-array-index-key, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-non-null-asserted-optional-chain */
import { Box, HStack, Stack, Text, Icon, Button } from '@chakra-ui/core';
import { IoCellular, IoWifi, IoBatteryHalf, IoChevronBack } from 'react-icons/io5';
import React, { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import moment from 'moment';
import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdOutlineCameraAlt,
  MdOutlineFileUpload,
  MdOutlineRadioButtonChecked,
  MdOutlineRadioButtonUnchecked,
  MdShare,
} from 'react-icons/md';
import { FaThumbsUp } from 'react-icons/fa';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { IconType } from 'react-icons';
import { useTranslation } from 'react-i18next';
import { QuestionnaireTemplateStore } from '../../store/questionnaireTemplateModel';
import ImageView from '../../../../../app/components/ImageView';
import { ImagePreviewModal } from '../../../../../app/components/ImagePreviewModal';
import { QUESTIONNAIRE_ELEMENT_TYPE } from '../../questionnaireTemplate.enums';
import { formElementSchema } from '../schemas/formElement.schema';
import { getConsistentDateFormat } from '../../../../../utils/dateUtils';

// Constants
const MOBILE_DIMENSIONS = {
  maxWidth: '375px',
  height: '700px',
  aspectRatio: '9/19',
  borderRadius: '20px',
} as const;

const COLORS = {
  border: '#E5E5E5',
  primary: '#2196F3',
  secondary: '#4D4D4D',
  accent: '#FF6711',
  gray: '#90A4AE',
  placeholder: '#7f7e7eff',
  header: '#455A64',
} as const;

// Types
interface ElementPreviewProps {
  icon?: IconType;
  questionId?: string;
  question?: string;
  title?: string;
  color?: string;
}

interface DragState {
  isDragging: boolean;
  startY: number;
  scrollTop: number;
}

// Custom hook for drag scrolling
const useDragScroll = (ref: React.RefObject<HTMLElement>) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    startY: 0,
    scrollTop: 0,
  });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      /* istanbul ignore next */
      if (!ref.current) return;

      setDragState({
        isDragging: true,
        startY: e.pageY,
        scrollTop: ref.current.scrollTop,
      });

      // Prevent text selection during drag
      e.preventDefault();
    },
    [ref],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragState.isDragging || !ref.current) return;

      const deltaY = e.pageY - dragState.startY;
      ref.current.scrollTop = dragState.scrollTop - deltaY;
    },
    [dragState, ref],
  );

  const handleMouseUp = useCallback(() => {
    setDragState((prev) => ({ ...prev, isDragging: false }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    setDragState((prev) => ({ ...prev, isDragging: false }));
  }, []);

  return {
    isDragging: dragState.isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
  };
};

const formatDateTime = () => {
  const now = new Date();
  return {
    date: getConsistentDateFormat(now, 'full'),
    time: moment(now).format('h:mm A'),
    clockTime: moment(now).format('h:mm'),
  };
};

export const MobilePreview = React.memo(function MobilePreview() {
  const scrollRef = useRef<HTMLElement>(null);
  const rootElementIds = QuestionnaireTemplateStore.useStoreState((state) => state.rootElementIds);
  const selectedElementId = QuestionnaireTemplateStore.useStoreState((state) => state.selectedElementId);

  const { isDragging, handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave } = useDragScroll(scrollRef);

  // Scroll to selected element when it changes
  useEffect(() => {
    if (selectedElementId && scrollRef.current) {
      const elementNode = scrollRef.current.querySelector(`[data-question-id="${selectedElementId}"]`);
      /* istanbul ignore else */
      if (elementNode) {
        elementNode.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [selectedElementId]);

  return (
    <Box pr={6}>
      <Box display="flex" flexDirection="column" {...MOBILE_DIMENSIONS} overflow="hidden" boxShadow="0 0 43.31px #00000026">
        <MobileHeader />
        <AppHeader />
        <TabNavigation />
        <Stack
          spacing={0}
          flex={1}
          overflowY="auto"
          ref={scrollRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          cursor={isDragging ? 'grabbing' : 'grab'}
          userSelect={isDragging ? 'none' : 'auto'}
          sx={{
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
          }}
          data-testid="mobile-preview-scrollable-box"
        >
          <WelcomeMessage />
          {rootElementIds.map((id) => (
            <ElementPreview key={id} questionId={id} />
          ))}
        </Stack>
        <MobileFooter />
      </Box>
    </Box>
  );
});

// Sub-components
const AppHeader = React.memo(() => {
  const { t } = useTranslation('questionnaireTemplate');
  return (
    <HStack px={3} justify="space-between" align="center" minH="39px" borderBottom={`1px solid ${COLORS.border}`}>
      <Text fontSize="lg">{t('formBuilder.preview.mobileView.headerTitle')}</Text>
      <Box
        rounded="full"
        height="22px"
        width="22px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        border={`2px solid ${COLORS.gray}`}
      >
        <Icon as={ChevronDownIcon} color={COLORS.gray} />
      </Box>
    </HStack>
  );
});

const TabNavigation = React.memo(() => {
  const { t } = useTranslation('questionnaireTemplate');
  return (
    <HStack justify="space-between" align="center" minH="40px" borderBottom={`1px solid ${COLORS.border}`}>
      <TabItem label={t('formBuilder.preview.mobileView.information')} isActive={false} />
      <TabItem label={t('formBuilder.preview.mobileView.messaging')} isActive />
    </HStack>
  );
});

const TabItem = React.memo(({ label, isActive }: { label: string; isActive: boolean }) => (
  <Box
    flex={1}
    height="100%"
    display="flex"
    alignItems="center"
    justifyContent="center"
    position="relative"
    _after={
      isActive
        ? {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '4px',
            backgroundColor: COLORS.primary,
            bottom: '0',
          }
        : undefined
    }
  >
    <Text color={isActive ? COLORS.primary : COLORS.secondary} fontSize="sm" fontWeight="bold">
      {label}
    </Text>
  </Box>
));

const WelcomeMessage = React.memo(() => {
  const { t } = useTranslation('questionnaireTemplate');
  return (
    <ElementPreview
      icon={FaThumbsUp}
      question={t('formBuilder.preview.mobileView.welcome.question')}
      title={t('formBuilder.preview.mobileView.welcome.title')}
      color={COLORS.accent}
    />
  );
});

const MessageIcon = React.memo(({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18.354 3H2.70033C2.49052 3 2.25 3.17555 2.25 3.38245V13.4239C2.25 13.6307 2.49052 13.8281 2.70033 13.8281H5.39062V17.995L9.61538 13.8281H18.354C18.5639 13.8281 18.7031 13.6307 18.7031 13.4239V3.38245C18.7031 3.17555 18.5639 3 18.354 3Z"
      fill="#2196F3"
    />
    <path
      d="M21.3934 5.95312H19.8748V13.7611C19.8748 14.5105 19.5507 15 18.6958 15H10.2231L8.39941 16.8281H14.4785L18.703 21V16.8281H21.3934C21.6033 16.8281 21.7498 16.6283 21.7498 16.4212V6.37983C21.7498 6.17292 21.6033 5.95312 21.3934 5.95312Z"
      fill="#2196F3"
    />
  </svg>
));

const ElementPreview = React.memo(function ElementPreview({
  icon,
  questionId,
  question,
  title,
  color = COLORS.primary,
}: ElementPreviewProps) {
  const { t } = useTranslation('questionnaireTemplate');
  const [open, setOpen] = useState(false);

  const selectedElementId = QuestionnaireTemplateStore.useStoreState((state) => state.selectedElementId);

  const elementData = QuestionnaireTemplateStore.useStoreState(
    useCallback(
      (state) => {
        if (!questionId) return null;
        return {
          question: state.getElementValue(questionId, 'question'),
          type: state.getElementValue(questionId, 'questionType'),
          images: state.getElementValue(questionId, 'images'),
          isSkippable: state.getElementValue(questionId, 'isSkippable'),
          maximumDocumentCount: state.getElementValue(questionId, 'maximumDocumentCount'),
        };
      },
      [questionId],
    ),
  );

  const isYesNo =
    elementData?.type === QUESTIONNAIRE_ELEMENT_TYPE.YESNO || elementData?.type === QUESTIONNAIRE_ELEMENT_TYPE.YESNO_WITH_IMAGE;
  const isMultiChoice =
    elementData?.type === QUESTIONNAIRE_ELEMENT_TYPE.MULTI_CHOICE ||
    elementData?.type === QUESTIONNAIRE_ELEMENT_TYPE.MULTI_CHOICE_WITH_IMAGE;
  const isFreeform = elementData?.type === QUESTIONNAIRE_ELEMENT_TYPE.FREE_FORM;
  const isDocumentUpload = elementData?.type === QUESTIONNAIRE_ELEMENT_TYPE.DOCUMENT_UPLOAD;
  const isVideoResponse = elementData?.type === QUESTIONNAIRE_ELEMENT_TYPE.VIDEO_RESPONSE;

  const dateTime = formatDateTime();

  const displayQuestion = question || elementData?.question || t('formBuilder.preview.mobileView.question.noQuestionText');
  const hasQuestion = !!(question || elementData?.question);
  const isSelected = questionId === selectedElementId;

  return (
    <>
      <HStack
        borderBottom={`1px solid ${COLORS.border}`}
        padding={4}
        align="flex-start"
        spacing={3}
        data-question-id={questionId}
        backgroundColor={isSelected ? '#f9fbfc' : 'transparent'}
        transition="background-color 0.3s ease"
        position="relative"
        _before={
          isSelected
            ? {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '4px',
                backgroundColor: COLORS.primary,
              }
            : undefined
        }
      >
        {icon ? <Icon as={icon} color={color} boxSize={6} /> : <MessageIcon size={26} />}
        <Stack flex={1} spacing={2}>
          <HStack align="flex-start" justify="space-between">
            <Text color={color} fontSize="sm" lineHeight="21px">
              {isVideoResponse
                ? t('formBuilder.preview.mobileView.videoResponse.header')
                : title || t('formBuilder.preview.mobileView.question.defaultTitle')}
            </Text>
            <Stack justifyContent="flex-end" spacing={0}>
              <Text fontSize="11px" color={COLORS.secondary} lineHeight={1.1} textAlign="right">
                {dateTime.date}
              </Text>
              <Text fontSize="11px" color={COLORS.secondary} lineHeight={1.1} textAlign="right">
                {dateTime.time}
              </Text>
            </Stack>
          </HStack>
          {!isVideoResponse && (
            <Text
              fontSize="sm"
              fontWeight="bold"
              lineHeight="21px"
              color={hasQuestion ? COLORS.secondary : COLORS.placeholder}
              wordBreak="break-word"
            >
              {displayQuestion}
            </Text>
          )}
          {isFreeform && <FreeFormInputPreview />}
          {elementData?.images?.length! > 0 && (
            <HStack justify="center">
              <ImageView
                src={elementData?.images?.[0]?.url}
                showAddImageIcon
                onClick={() => {
                  setOpen(true);
                }}
                shape="rounded"
                height="100px"
              />
              <ImagePreviewModal
                setTrigger={open}
                images={elementData?.images}
                getImageKey={/* istanbul ignore next */ (image) => image?.id || ''}
                getImageSrc={/* istanbul ignore next */ (image) => image?.url || ''}
                onModalClose={() => setOpen(false)}
              />
            </HStack>
          )}
          {isYesNo && <YesNoPreview type={elementData?.type} />}
          {isMultiChoice && questionId && <MultiChoicePreview questionId={questionId} />}
          {isDocumentUpload && (
            <DocumentUploadPreview
              skipQuestion={elementData?.isSkippable ?? /* istanbul ignore next */ false}
              maximumDocumentCount={elementData?.maximumDocumentCount}
            />
          )}

          {isVideoResponse && questionId && (
            <VideoResponsePreview
              questionId={questionId}
              skipQuestion={elementData?.isSkippable ?? /* istanbul ignore next */ false}
            />
          )}
        </Stack>
      </HStack>
      {questionId && <BranchPreview questionId={questionId} />}
    </>
  );
});

const MobileHeader = React.memo(() => {
  const { t } = useTranslation('questionnaireTemplate');
  const { clockTime } = useMemo(() => formatDateTime(), []);

  return (
    <>
      <HStack
        height="50px"
        justify="space-between"
        align="center"
        color="#090A0A"
        backgroundColor="#fff"
        paddingX="28px"
        flexShrink={0}
      >
        <Text fontSize="15.2px" fontWeight="500">
          {clockTime}
        </Text>
        <HStack spacing={1}>
          <IoCellular size="18px" />
          <IoWifi size="18px" />
          <IoBatteryHalf size="18px" />
        </HStack>
      </HStack>
      <HStack
        backgroundColor={COLORS.header}
        color="#fff"
        height="53px"
        justify="space-between"
        align="center"
        paddingX={3}
        flexShrink={0}
      >
        <HStack>
          <IoChevronBack />
          <Text fontSize="15.2px" fontWeight="600">
            {t('formBuilder.preview.mobileView.title')}
          </Text>
        </HStack>
        <Icon as={MdShare} />
      </HStack>
    </>
  );
});

const MobileFooter = React.memo(() => (
  <HStack
    height="32.3px"
    justify="center"
    align="center"
    color="#090A0A"
    backgroundColor="#fff"
    paddingX="28px"
    flexShrink={0}
    data-testid="mobile-preview-footer"
  >
    <svg width="142" height="5" viewBox="0 0 142 5" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.800781 2.4248C0.800781 1.11313 1.86411 0.0498047 3.17578 0.0498047H139.026C140.337 0.0498047 141.401 1.11313 141.401 2.4248C141.401 3.73648 140.337 4.79981 139.026 4.79981H3.17578C1.86411 4.79981 0.800781 3.73648 0.800781 2.4248Z"
        fill="#090A0A"
      />
    </svg>
  </HStack>
));

const BranchPreview = React.memo(({ questionId }: { questionId: string }) => {
  const elementData = QuestionnaireTemplateStore.useStoreState(
    useCallback(
      (state) => {
        /* istanbul ignore next */
        if (!questionId) return null;
        return {
          preferredAnswer: state.getElementValue(questionId, 'preferredAnswer'),
        };
      },
      [questionId],
    ),
  );
  const ordering = QuestionnaireTemplateStore.useStoreState((state) => state.ordering);

  if (!elementData || !elementData.preferredAnswer) return null;
  const branchName = elementData.preferredAnswer;
  const branchKey = `${questionId}-${branchName}`;
  const branchChildren = ordering[branchKey] ?? [];
  if (!branchChildren.length) return null;
  return (
    <>
      {branchChildren.map((childId) => (
        <ElementPreview key={childId} questionId={childId} />
      ))}
    </>
  );
});

const YesNoPreview = React.memo(({ type }: { type: QUESTIONNAIRE_ELEMENT_TYPE }) => {
  const { t } = useTranslation('questionnaireTemplate');
  const elementSchema = formElementSchema[type];
  if (!elementSchema?.branches?.length) return null;

  return (
    <HStack margin={0}>
      {elementSchema.branches?.map((_, i) => (
        <ButtonPreview key={i} text={t(`formBuilder.schema.yesno.branches.${i}`)} color={COLORS.primary} />
      ))}
    </HStack>
  );
});

const ButtonPreview = React.memo(({ text, color, icon }: { text: string; color?: string | undefined; icon?: IconType }) => {
  return (
    <Button
      variant="outline"
      size="sm"
      borderRadius={20}
      data-testid={`mobile-preview-button-${text.toLowerCase()}`}
      sx={color ? { outlineColor: color } : {}}
    >
      <Text textColor={color}>{text}</Text>
      {icon && <Icon as={icon} color={color} ml={4} />}
    </Button>
  );
});

const MultiChoicePreview = React.memo(({ questionId }: { questionId: string }) => {
  const { t } = useTranslation('questionnaireTemplate');
  const elementData = QuestionnaireTemplateStore.useStoreState(
    useCallback(
      (state) => {
        /* istanbul ignore next */
        if (!questionId) return null;
        return {
          options: state.getElementValue(questionId, 'options'),
          totalAnswer: state.getElementValue(questionId, 'totalAnswer'),
        };
      },
      [questionId],
    ),
  );
  const isSingleChoice = elementData?.totalAnswer !== 'any' && (elementData?.totalAnswer ?? /* istanbul ignore next */ 0) < 2;

  return (
    <Stack margin={0}>
      <Text fontSize="sm" color={COLORS.gray}>
        (
        {isSingleChoice
          ? t('formBuilder.preview.mobileView.mcqSelectSingle')
          : t('formBuilder.preview.mobileView.mcqSelectMultiple')}
        )
      </Text>
      {elementData?.options?.map((option, i) => {
        const hasOptionText = Boolean(option.text);

        let IconComponent;

        if (isSingleChoice) {
          IconComponent = option.isPreferredAnswer ? MdOutlineRadioButtonChecked : MdOutlineRadioButtonUnchecked;
        } else {
          IconComponent = option.isPreferredAnswer ? MdCheckBox : MdCheckBoxOutlineBlank;
        }
        return (
          <HStack key={option.id} p={2} borderRadius="md" border="1px solid #f5f3f6" background="#fcfafd">
            <Icon as={IconComponent} flexShrink={0} boxSize={4} />
            <Text color={hasOptionText ? COLORS.secondary : COLORS.placeholder} wordBreak="break-word">
              {option.text || t('formBuilder.preview.mobileView.optionNoText', { index: i + 1 })}
            </Text>
          </HStack>
        );
      })}
    </Stack>
  );
});

const FreeFormInputPreview = React.memo(() => {
  const { t } = useTranslation('questionnaireTemplate');
  return (
    <Box p={2} borderRadius="md" border="1px solid #f5f3f6" background="#fcfafd">
      <Text color={COLORS.placeholder} fontSize="sm" data-testid="freeform-input-preview">
        {t('formBuilder.preview.mobileView.question.writeQuestionText')}
      </Text>
    </Box>
  );
});

const DocumentUploadPreview = React.memo(
  ({ skipQuestion, maximumDocumentCount }: { skipQuestion: boolean; maximumDocumentCount?: number }) => {
    const { t } = useTranslation('questionnaireTemplate');

    return (
      <Stack spacing={2}>
        <Text color={COLORS.placeholder} fontSize="sm" data-testid="document-upload-info">
          {t('formBuilder.preview.mobileView.documentUpload.info', {
            count: maximumDocumentCount || 1,
          })}
        </Text>
        <HStack>
          <ButtonPreview
            text={t('formBuilder.preview.mobileView.documentUpload.upload')}
            icon={MdOutlineFileUpload}
            color={COLORS.primary}
          />
          <ButtonPreview text={t('formBuilder.preview.mobileView.documentUpload.capture')} icon={MdOutlineCameraAlt} />
        </HStack>
        {skipQuestion && (
          <Text fontSize="sm" textDecoration="underline" color="#2196F3" data-testid="document-upload-skip-info">
            {t('formBuilder.preview.mobileView.documentUpload.skipQuestionInfo')}
          </Text>
        )}
      </Stack>
    );
  },
);
const VideoResponsePreview = React.memo(function VideoResponsePreview({
  questionId,
  skipQuestion,
}: {
  questionId: string;
  skipQuestion: boolean;
}) {
  const { t } = useTranslation('questionnaireTemplate');

  const { question, maximumVideoDuration } = QuestionnaireTemplateStore.useStoreState(
    React.useCallback(
      (state) => ({
        question: state.getElementValue(questionId, 'question'),
        maximumVideoDuration: state.getElementValue(questionId, 'maximumVideoDuration'),
      }),
      [questionId],
    ),
  );

  const hasQuestion = Boolean(question);

  return (
    <Stack spacing={3}>
      <Text fontSize="sm" color={COLORS.secondary} data-testId="video-response-instruction-text">
        {t('formBuilder.preview.mobileView.videoResponse.instruction', {
          count: maximumVideoDuration || 1,
        })}
      </Text>

      <Box bg="#e3f2fd" p={3} borderRadius="md">
        <Text fontWeight="bold" fontSize="sm" color={COLORS.primary}>
          {t('formBuilder.preview.mobileView.videoResponse.questionTitle')}
        </Text>

        <Text fontSize="sm" mt={1} color={hasQuestion ? COLORS.secondary : COLORS.placeholder} wordBreak="break-word">
          {hasQuestion ? question : t('formBuilder.preview.mobileView.question.noQuestionText')}
        </Text>
      </Box>

      <Text fontSize="xs" color={COLORS.gray}>
        {t('formBuilder.preview.mobileView.videoResponse.footerNote')}
      </Text>

      <Button size="sm" borderRadius="20px" alignSelf="flex-end" backgroundColor={COLORS.primary} color="white">
        {t('formBuilder.preview.mobileView.videoResponse.start')}
      </Button>

      {skipQuestion && (
        <Text fontSize="sm" textDecoration="underline" color={COLORS.primary} data-testid="video-response-skip-info">
          {t('formBuilder.preview.mobileView.videoResponse.skipQuestionInfo')}
        </Text>
      )}
    </Stack>
  );
});
